import React, { useState, useEffect } from 'react';
import { ArrowUpRight, Loader, ArrowLeft, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { cn } from '@/lib/utils';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { getFile } from '../../services/fileSystemService';
import { getFileUrl, getShareNavigationPath } from '../../lib/routingUtils';
import { FileType, CompactSegment } from '../../types';
import type { RootState } from '../../reducers';
import {
  getBubbleBaseStyles,
  getBubbleRoleStyles,
} from '../selectedVideo/chat/history/bubbleStyles';

interface ReferenceButtonProps {
  fileId: string;
  timestamp: string;
  isActive?: boolean;
  onActiveChange?: (active: boolean) => void;
  onShowTranscript?: () => void;
  inlineMode?: boolean;
  onBack?: () => void;
}

const formatTimestamp = (seconds: string): string => {
  const totalSeconds = parseInt(seconds, 10);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const remainingSeconds = totalSeconds % 60;

  if (hours > 0) {
    return `${hours.toString().padStart(2, '0')}:${minutes
      .toString()
      .padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  }
  return `${minutes
    .toString()
    .padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
};

export const ReferenceButton: React.FC<ReferenceButtonProps> = ({
  fileId,
  timestamp,
  isActive = false,
  onActiveChange,
  onShowTranscript,
  inlineMode = false,
  onBack,
}) => {
  const navigate = useNavigate();
  const { team } = useSelector((state: RootState) => state);
  const teamId = team.selectedTeam?.teamId;

  const [file, setFile] = useState<FileType | null>(null);
  const [transcript, setTranscript] = useState<CompactSegment[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasAttemptedFetch, setHasAttemptedFetch] = useState(false);

  const timestampSeconds = timestamp ? parseInt(timestamp, 10) : 0;

  const handleNavigateToVideo = (time?: number) => {
    if (!teamId) return;

    const targetTime = time !== undefined ? time : timestampSeconds;

    // Check if this is a shared file (short ID)
    const isSharedFile = fileId.length < 20;
    if (isSharedFile) {
      // Use full page redirect for shared videos to avoid hash routing
      window.location.href = getShareNavigationPath(fileId, { t: targetTime });
    } else {
      // Use the proper routing utility for team files
      const fileUrl = getFileUrl({
        teamId,
        parentId: 'default',
        fileId,
      });
      navigate(`${fileUrl}?t=${targetTime}`);
    }

    // Close the popover
    onActiveChange?.(false);
  };

  // Find the segment that contains this timestamp and surrounding segments
  const findSegmentsWithContext = (
    segments: CompactSegment[],
    time: number,
  ): {
    before: CompactSegment | null;
    current: CompactSegment | null;
    after: CompactSegment | null;
  } => {
    if (!segments || segments.length === 0)
      return { before: null, current: null, after: null };

    const currentIndex = segments.findIndex((segment) => {
      return time >= segment.start && time <= segment.end;
    });

    if (currentIndex === -1) {
      return { before: null, current: null, after: null };
    }

    return {
      before: currentIndex > 0 ? segments[currentIndex - 1] : null,
      current: segments[currentIndex],
      after:
        currentIndex < segments.length - 1 ? segments[currentIndex + 1] : null,
    };
  };

  const { before, current, after } = findSegmentsWithContext(
    transcript,
    timestampSeconds,
  );

  // Fetch when popover is opened (isActive) OR when in inline mode
  useEffect(() => {
    const fetchFileData = async () => {
      if (!fileId || (!isActive && !inlineMode) || hasAttemptedFetch) {
        return;
      }

      setIsLoading(true);
      setError(null);
      setHasAttemptedFetch(true);

      try {
        const response = await getFile(fileId);
        const fileData = response?.file;
        const transcriptData = response?.transcript?.compact_segments || [];

        if (fileData) {
          setFile(fileData);
          setTranscript(transcriptData);
        } else {
          setError('File not found in response');
        }
      } catch (err) {
        console.error('Error fetching file:', err);
        setError('Failed to load file');
      } finally {
        setIsLoading(false);
      }
    };

    fetchFileData();
  }, [fileId, isActive, inlineMode, hasAttemptedFetch]);

  const PreviewContent = () => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center py-8">
          <Loader className="h-5 w-5 animate-spin text-muted-foreground/50" />
        </div>
      );
    }

    if (error || !file) {
      return (
        <div className="text-sm text-muted-foreground">
          {error || 'File not found'}
        </div>
      );
    }

    return (
      <div className="w-80">
        {/* File name and timestamp */}
        <button
          type="button"
          onClick={() => handleNavigateToVideo()}
          className="w-full mb-3 pb-3 border-b text-left hover:bg-accent/50 -mx-4 px-4 transition-colors"
        >
          <div className="text-sm font-medium">{file.name}</div>
          <div className="text-xs text-muted-foreground">
            {formatTimestamp(timestamp || '0')}
          </div>
        </button>

        {/* Transcript Segments with Context */}
        {current && (
          <div className="space-y-2 text-sm">
            {/* Before segment */}
            {before && (
              <button
                type="button"
                onClick={() => handleNavigateToVideo(before.start)}
                className="text-left text-muted-foreground hover:text-foreground hover:bg-accent/50 -mx-4 px-4 py-1 w-[calc(100%+2rem)] transition-colors rounded"
              >
                {before.text}
              </button>
            )}

            {/* Current segment - highlighted */}
            <button
              type="button"
              onClick={() => handleNavigateToVideo(current.start)}
              className="text-left font-medium hover:bg-accent/50 -mx-4 px-4 py-1 w-[calc(100%+2rem)] transition-colors rounded"
            >
              {current.text}
            </button>

            {/* After segment */}
            {after && (
              <button
                type="button"
                onClick={() => handleNavigateToVideo(after.start)}
                className="text-left text-muted-foreground hover:text-foreground hover:bg-accent/50 -mx-4 px-4 py-1 w-[calc(100%+2rem)] transition-colors rounded"
              >
                {after.text}
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  // Inline mode: Render as a bubble with transcript
  if (inlineMode) {
    return (
      <div
        className={cn(
          getBubbleBaseStyles(),
          getBubbleRoleStyles('assistant'),
          'max-w-[85%] w-full',
        )}
      >
        {/* Header with back button */}
        <div className="flex items-center gap-2 mb-3 pb-2.5 border-b border-border/50">
          <Button
            variant="ghost"
            size="sm"
            onClick={onBack}
            className="h-7 w-7 p-0 -ml-1 hover:bg-accent/50 transition-colors flex-shrink-0"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="flex-1 min-w-0 overflow-hidden">
            <div className="text-sm font-semibold truncate">
              {file?.name && file.name.length > 20
                ? `${file.name.slice(0, 20)}...`
                : file?.name}
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleNavigateToVideo()}
            className="h-7 w-7 p-0 hover:bg-accent/50 transition-colors flex-shrink-0"
          >
            <ExternalLink className="h-4 w-4" />
          </Button>
        </div>

        {/* Content */}
        {(() => {
          if (isLoading) {
            return (
              <div className="flex items-center justify-center py-8">
                <Loader className="h-5 w-5 animate-spin text-muted-foreground/50" />
              </div>
            );
          }

          if (error || !file) {
            return (
              <div className="text-sm text-muted-foreground py-4 text-center">
                {error || 'File not found'}
              </div>
            );
          }

          return (
            <div className="space-y-2 text-sm">
              {/* Before segment */}
              {before && (
                <button
                  type="button"
                  onClick={() => handleNavigateToVideo(before.start)}
                  className="text-left w-full text-muted-foreground hover:text-foreground hover:bg-accent/30 -mx-3 px-3 py-2 transition-colors rounded-md group"
                >
                  <div className="text-xs text-muted-foreground/60 mb-1">
                    {formatTimestamp(before.start.toString())}
                  </div>
                  <div className="leading-relaxed">{before.text}</div>
                </button>
              )}

              {/* Current segment - highlighted */}
              {current && (
                <button
                  type="button"
                  onClick={() => handleNavigateToVideo(current.start)}
                  className="text-left w-full font-medium bg-accent/20 hover:bg-accent/40 -mx-3 px-3 py-2 transition-colors rounded-md border-l-2 border-primary group"
                >
                  <div className="text-xs text-primary/80 font-semibold mb-1">
                    {formatTimestamp(current.start.toString())}
                  </div>
                  <div className="leading-relaxed">{current.text}</div>
                </button>
              )}

              {/* After segment */}
              {after && (
                <button
                  type="button"
                  onClick={() => handleNavigateToVideo(after.start)}
                  className="text-left w-full text-muted-foreground hover:text-foreground hover:bg-accent/30 -mx-3 px-3 py-2 transition-colors rounded-md group"
                >
                  <div className="text-xs text-muted-foreground/60 mb-1">
                    {formatTimestamp(after.start.toString())}
                  </div>
                  <div className="leading-relaxed">{after.text}</div>
                </button>
              )}
            </div>
          );
        })()}
      </div>
    );
  }

  // Default popover mode
  return (
    <Popover
      open={isActive}
      onOpenChange={(open) => {
        onActiveChange?.(open);
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          onClick={(e) => {
            // If onShowTranscript is provided, use inline mode instead of popover
            if (onShowTranscript) {
              e.preventDefault();
              e.stopPropagation();
              onShowTranscript();
            }
          }}
          className={cn(
            'inline cursor-pointer align-middle transition-transform duration-150 hover:scale-110 border-0 bg-transparent p-0',
          )}
          title={
            file?.name
              ? `Show source: ${file.name} at ${formatTimestamp(timestamp)}`
              : 'Show source'
          }
        >
          <ArrowUpRight
            size={18}
            className={cn(
              'relative -top-0.5 inline-block px-0.5 align-middle transition-colors duration-150',
              isActive ? 'text-primary' : 'text-gray-400',
            )}
            strokeWidth={1.5}
          />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-auto p-0 max-h-[70vh] overflow-y-auto"
        side="top"
        align="start"
        sideOffset={8}
        alignOffset={-10}
        avoidCollisions
        collisionPadding={20}
      >
        <div className="p-4 max-w-md">
          <PreviewContent />
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default ReferenceButton;
